import type { ActionChip, SelectionKind } from "./types";
import {
  AddIconIcon,
  CaptionIcon,
  ConciseIcon,
  CreateSlidesIcon,
  ExpandTextIcon,
  FixAlignIcon,
  InfographicIcon,
  InsightsIcon,
  PencilIcon,
  PhotoHeadIcon,
  SearchIcon,
  SpeakerNotesIcon,
  SummarizeIcon,
  ToChartIcon,
  ToTableIcon,
  ToneIcon,
  TransformSlideIcon,
  VectorizeIcon,
} from "./chipIcons";

/**
 * How many chips sit in the visible tray. Anything beyond this moves into the
 * "···" overflow menu, so the dock stays one row tall in the narrow task pane
 * no matter how many actions a selection offers.
 */
export const VISIBLE_CHIP_COUNT = 3;

/* ── Chip definitions, one per action ─────────────────────────────────────── */

const CREATE_SLIDES: ActionChip = { id: "createSlides", label: "Create slides", Icon: CreateSlidesIcon };
const TRANSFORM_SLIDE: ActionChip = { id: "transformSlide", label: "Transform slide", Icon: TransformSlideIcon };
const FIX_ALIGNMENT: ActionChip = { id: "fixAlignment", label: "Fix alignment", Icon: FixAlignIcon };
const INFOGRAPHIC: ActionChip = { id: "infographic", label: "Infographic", Icon: InfographicIcon };
const GET_INSIGHTS: ActionChip = { id: "getInsights", label: "Get Insights", Icon: InsightsIcon };
const SPEAKER_NOTES: ActionChip = { id: "speakerNotes", label: "Speaker notes", Icon: SpeakerNotesIcon };
const IMPROVEMENTS: ActionChip = { id: "improvements", label: "Improvements", Icon: SearchIcon };

const IMPROVE_WORDING: ActionChip = { id: "improveWording", label: "Improve wording", Icon: PencilIcon };
const MAKE_CONCISE: ActionChip = { id: "makeConcise", label: "Make concise", Icon: ConciseIcon };
const EXPAND_TEXT: ActionChip = { id: "expandText", label: "Expand text", Icon: ExpandTextIcon };
const CHANGE_TONE: ActionChip = { id: "changeTone", label: "Change tone", Icon: ToneIcon };
const ADD_ICON: ActionChip = { id: "addIcon", label: "Add icon", Icon: AddIconIcon };
/** Same action as ADD_ICON, pluralised for a multi-shape selection. */
const ADD_ICONS: ActionChip = { id: "addIcon", label: "Add icons", Icon: AddIconIcon };

const TABLE_TO_CHART: ActionChip = { id: "tableToChart", label: "Convert to chart", Icon: ToChartIcon };
const SUMMARIZE_TABLE: ActionChip = { id: "summarizeTable", label: "Summarize table", Icon: SummarizeIcon };
const FIND_ANOMALIES: ActionChip = { id: "findAnomalies", label: "Find anomalies", Icon: SearchIcon };

const IMAGE_TO_CHART: ActionChip = { id: "imageToChart", label: "Convert to chart", Icon: ToChartIcon };
const IMAGE_TO_TABLE: ActionChip = { id: "imageToTable", label: "Convert to table", Icon: ToTableIcon };
const PHOTO_HEAD: ActionChip = { id: "photoHead", label: "Photo head", Icon: PhotoHeadIcon };
const VECTORIZE: ActionChip = { id: "vectorize", label: "Vectorize", Icon: VectorizeIcon };
const REMOVE_BACKGROUND: ActionChip = { id: "removeBackground", label: "Remove background", Icon: VectorizeIcon };
const ADD_CAPTION: ActionChip = { id: "addCaption", label: "Add caption", Icon: CaptionIcon };
const SKETCH_TO_SLIDE: ActionChip = { id: "sketchToSlide", label: "Sketch to Slide", Icon: PencilIcon };

/* ── Selection → chips ────────────────────────────────────────────────────── */

/**
 * The chip set offered for each selection kind, in priority order: the first
 * {@link VISIBLE_CHIP_COUNT} render in the tray, the rest in the overflow menu.
 *
 * The plain `image` set is the union offered before the vision classifier has
 * answered (or when it can't): its three visible chips are the ones that apply
 * to any picture, with the content-specific conversions in the overflow, so a
 * picture is never unusable while the classification is in flight.
 *
 * The five `image*` sets mirror `_ACTIONS_BY_TYPE` in the backend's
 * `classify_image_service.py` exactly — same actions, same order. The classifier
 * returns its own `actions` list, but it carries no icons and its action names
 * are its own; resolving the set locally from `image_type` keeps one registry of
 * label + icon + routing instead of two that can disagree. `chipConfig.test.ts`
 * pins these against the backend's sets so a divergence fails CI rather than
 * shipping a chip the API never meant to offer.
 *
 * "Search actions" is intentionally absent: the command palette is already
 * reachable from its own shortcut and the Deckorate tab, so spending a chip
 * slot on it would push a real action into the overflow.
 */
export const SELECTION_CHIPS: Readonly<Record<SelectionKind, readonly ActionChip[]>> = {
  slide: [
    CREATE_SLIDES,
    TRANSFORM_SLIDE,
    FIX_ALIGNMENT,
    INFOGRAPHIC,
    GET_INSIGHTS,
    SPEAKER_NOTES,
    IMPROVEMENTS,
  ],
  textbox: [
    IMPROVE_WORDING,
    MAKE_CONCISE,
    ADD_ICON,
    EXPAND_TEXT,
    CHANGE_TONE,
    FIX_ALIGNMENT,
    GET_INSIGHTS,
  ],
  table: [
    TABLE_TO_CHART,
    INFOGRAPHIC,
    GET_INSIGHTS,
    SUMMARIZE_TABLE,
    FIND_ANOMALIES,
    FIX_ALIGNMENT,
  ],
  chart: [GET_INSIGHTS, INFOGRAPHIC, FIX_ALIGNMENT],
  image: [
    VECTORIZE,
    REMOVE_BACKGROUND,
    GET_INSIGHTS,
    ADD_CAPTION,
    PHOTO_HEAD,
    IMAGE_TO_CHART,
    IMAGE_TO_TABLE,
  ],
  imageChart: [IMAGE_TO_CHART, GET_INSIGHTS, VECTORIZE],
  imageTable: [IMAGE_TO_TABLE, GET_INSIGHTS],
  imagePortrait: [PHOTO_HEAD, REMOVE_BACKGROUND],
  imageSketch: [SKETCH_TO_SLIDE, GET_INSIGHTS],
  imageGeneral: [VECTORIZE, REMOVE_BACKGROUND, GET_INSIGHTS, ADD_CAPTION],
  shape: [FIX_ALIGNMENT, ADD_ICON, GET_INSIGHTS],
  multi: [
    ADD_ICONS,
    IMPROVE_WORDING,
    VECTORIZE,
    FIX_ALIGNMENT,
    GET_INSIGHTS,
  ],
};

/**
 * Label for the "Slide N · <selection>" context line above the tray.
 *
 * The classifier also returns a ready-to-render `banner_label`; the dock prefers
 * that when it has one (see `usePptSelectionKind`). These are the fallbacks —
 * used before the classifier answers, and if it never does.
 */
export function selectionLabel(kind: SelectionKind, shapeCount: number): string {
  switch (kind) {
    case "slide":
      return "Nothing selected";
    case "textbox":
      return "Text box";
    case "table":
      return "Table";
    case "chart":
      return "Chart";
    case "image":
      return "Image";
    case "imageChart":
      return "Image · chart";
    case "imageTable":
      return "Image · table";
    case "imagePortrait":
      return "Image · portrait";
    case "imageSketch":
      return "Image · sketch";
    case "imageGeneral":
      return "Image";
    case "shape":
      return "Shape";
    case "multi":
      // Pluralised defensively. `multi` only ever carries 2+ today, but the count
      // originates from the host and a label reading "1 shapes" is the kind of
      // detail that outlives the assumption behind it.
      return `${shapeCount} ${shapeCount === 1 ? "shape" : "shapes"}`;
  }
}
